/**
 * El tema de la pantalla: claro u oscuro.
 *
 * Sin lógica de React, para poder probarla con el ejecutor de Node. Tres cosas
 * viven acá: qué tema corresponde dada la preferencia guardada y la del
 * sistema, cómo se lee y se guarda esa preferencia, y cómo se aplica.
 *
 * La preferencia es `sistema` hasta que la persona elige. Elegir una vez
 * guarda `claro` u `oscuro` y deja de seguir al sistema: quien cambia a mano
 * lo hace porque quiere algo distinto de lo que le da el equipo, y que cambie
 * solo al caer la tarde sería lo contrario de lo que pidió.
 */

export type Preferencia = 'sistema' | 'claro' | 'oscuro';
export type Tema = 'claro' | 'oscuro';

export const CLAVE_TEMA = 'vox.tema';

export function resolverTema(preferencia: Preferencia, sistemaEsClaro: boolean): Tema {
  if (preferencia === 'claro' || preferencia === 'oscuro') return preferencia;
  return sistemaEsClaro ? 'claro' : 'oscuro';
}

/** Lo que hay guardado, o `sistema` si no hay nada o el almacenamiento falla. */
export function leerPreferencia(almacen?: Pick<Storage, 'getItem'>): Preferencia {
  try {
    const guardado = (almacen ?? localStorage).getItem(CLAVE_TEMA);
    return guardado === 'claro' || guardado === 'oscuro' ? guardado : 'sistema';
  } catch {
    // Modo privado, datos de sitio bloqueados: el panel tiene que seguir
    // funcionando, solo que sin recordar la elección.
    return 'sistema';
  }
}

export function guardarPreferencia(preferencia: Preferencia, almacen?: Pick<Storage, 'setItem' | 'removeItem'>): void {
  try {
    const destino = almacen ?? localStorage;
    if (preferencia === 'sistema') destino.removeItem(CLAVE_TEMA);
    else destino.setItem(CLAVE_TEMA, preferencia);
  } catch {
    // Sin dónde guardar, la elección vale solo para esta sesión.
  }
}

/** El tema al que lleva el botón: el contrario del que se está viendo. */
export function alternar(actual: Tema): Tema {
  return actual === 'claro' ? 'oscuro' : 'claro';
}

export function aplicarTema(tema: Tema, raiz: Pick<HTMLElement, 'setAttribute'> = document.documentElement): void {
  raiz.setAttribute('data-tema', tema);
}
